import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Loader2, ArrowLeft, Check, X } from "lucide-react";

/**
 * Detailed report for a single bulk-upload run. Shows every row with status
 * + failure reason, plus the column mapping and hardcoded fields used.
 */
export default function AdminBulkUploadLog() {
  const { id } = useParams();
  const [log, setLog] = useState(null);
  const [filter, setFilter] = useState("all"); // all | ok | failed

  useEffect(() => {
    api.get(`/admin/products/bulk-upload/logs/${id}`).then(({ data }) => setLog(data));
  }, [id]);

  if (!log) {
    return (
      <div className="flex items-center justify-center py-24 text-[hsl(var(--kb-muted))]">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading…
      </div>
    );
  }

  const rows = (log.results || []).filter((r) => filter === "all" || r.status === filter);

  return (
    <div className="space-y-6" data-testid="bulk-log-detail">
      <div>
        <Link to="/admin/products/bulk-upload" className="text-xs text-[hsl(var(--kb-muted))] inline-flex items-center gap-1 hover:text-[hsl(var(--kb-primary))]">
          <ArrowLeft size={12} /> Back to bulk upload
        </Link>
        <h1 className="font-display text-3xl mt-1">{log.filename || "Upload"}</h1>
        <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">
          Started {new Date(log.started_at).toLocaleString()} · Finished {new Date(log.finished_at).toLocaleString()}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3 max-w-md">
        <Stat label="Total" value={log.total_rows} />
        <Stat label="Created" value={log.created_count} tone="ok" />
        <Stat label="Failed" value={log.failed_count} tone={log.failed_count ? "bad" : "muted"} />
      </div>

      {/* Mapping snapshot */}
      <details className="bg-white border border-[hsl(var(--kb-border))]">
        <summary className="px-5 py-3 cursor-pointer font-medium">Column mapping used</summary>
        <div className="p-5 grid md:grid-cols-2 gap-2 text-xs" data-testid="bulk-log-mapping">
          {Object.entries(log.mapping || {}).map(([k, v]) => (
            <div key={k}><span className="text-[hsl(var(--kb-muted))]">{k}:</span> <span className="font-mono">{v}</span></div>
          ))}
        </div>
      </details>

      {Object.keys(log.hardcoded || {}).filter((k) => log.hardcoded[k]).length > 0 && (
        <details className="bg-white border border-[hsl(var(--kb-border))]">
          <summary className="px-5 py-3 cursor-pointer font-medium">Hardcoded fields</summary>
          <div className="p-5 grid md:grid-cols-2 gap-2 text-xs" data-testid="bulk-log-hardcoded">
            {Object.entries(log.hardcoded).filter(([, v]) => v).map(([k, v]) => (
              <div key={k}><span className="text-[hsl(var(--kb-muted))]">{k}:</span> <span className="font-mono">{String(v)}</span></div>
            ))}
          </div>
        </details>
      )}

      {/* Filter + table */}
      <div className="bg-white border border-[hsl(var(--kb-border))]">
        <div className="px-5 py-3 border-b border-[hsl(var(--kb-border))] flex items-center justify-between gap-3">
          <div className="font-medium">Per-row results</div>
          <div className="flex gap-1 text-xs">
            {[
              { k: "all", label: "All" },
              { k: "ok", label: "Created" },
              { k: "failed", label: "Failed" },
            ].map((o) => (
              <button
                key={o.k}
                type="button"
                onClick={() => setFilter(o.k)}
                className={`px-3 py-1 border ${filter === o.k ? "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]" : "border-[hsl(var(--kb-border))]"}`}
                data-testid={`bulk-log-filter-${o.k}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div className="max-h-[600px] overflow-auto">
          <table className="w-full text-sm" data-testid="bulk-log-rows">
            <thead className="bg-[hsl(var(--kb-surface-alt))] sticky top-0">
              <tr>
                <th className="p-3 text-left">Row #</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.row_number} className={r.status === "failed" ? "bg-red-50" : ""}>
                  <td className="p-3 align-top font-mono text-xs">{r.row_number}</td>
                  <td className="p-3 align-top">
                    {r.status === "ok"
                      ? <span className="text-green-700 inline-flex items-center gap-1 text-xs"><Check size={12} /> Created</span>
                      : <span className="text-red-700 inline-flex items-center gap-1 text-xs"><X size={12} /> Failed</span>}
                  </td>
                  <td className="p-3 align-top text-xs">
                    {r.status === "ok"
                      ? <Link to={`/product/${r.product_id}`} className="underline text-[hsl(var(--kb-muted))]">{r.product_id}</Link>
                      : <span className="text-red-700">{r.error}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls = tone === "ok" ? "text-green-700"
    : tone === "bad" ? "text-red-700"
    : tone === "muted" ? "text-[hsl(var(--kb-muted))]"
    : "text-[hsl(var(--kb-text))]";
  return (
    <div className="bg-white border border-[hsl(var(--kb-border))] p-4">
      <div className="text-xs text-[hsl(var(--kb-muted))] uppercase tracking-wider">{label}</div>
      <div className={`text-2xl font-display ${cls}`}>{value}</div>
    </div>
  );
}
